import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { ArrowLeft, Check, ChevronsUpDown, CheckCircle2, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { COUNTRIES } from '@/data/countries';

const RESEND_SECONDS = 60;

function CountryPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between rounded-xl h-11 border-gray-200 font-normal text-left"
          data-testid="interest-country-trigger"
        >
          <span className={value ? 'text-[#1F2937]' : 'text-gray-400'}>{value || 'Select country'}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Search country..." data-testid="interest-country-search" />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c}
                  value={c}
                  onSelect={() => { onChange(c); setOpen(false); }}
                  data-testid={`interest-country-option-${c.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c ? 'opacity-100' : 'opacity-0'}`} />
                  {c}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function BringToSchoolPage() {
  const [form, setForm] = useState({ school_name: '', city: '', country: '', email: '', website: '' });
  const [guardian, setGuardian] = useState(false);
  const [step, setStep] = useState('form');          // form | code | done
  const [requestId, setRequestId] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const ready = useMemo(() => (
    form.school_name.trim() && form.city.trim() && form.country && form.email.trim() && guardian
  ), [form, guardian]);

  const startCooldown = () => {
    setCooldown(RESEND_SECONDS);
    const t = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) { clearInterval(t); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    try {
      const res = await api.post('/school-interest/start', { ...form, is_guardian: guardian });
      if (res.data.already_recorded || !res.data.request_id) {
        setStep('done');
      } else {
        setRequestId(res.data.request_id);
        setStep('code');
        startCooldown();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the verification code');
    }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post('/school-interest/verify', { request_id: requestId, code });
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not verify that code');
    }
    setBusy(false);
  };

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try {
      await api.post('/school-interest/resend', { request_id: requestId });
      toast.success('A new code is on its way');
      startCooldown();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not resend the code');
    }
    setBusy(false);
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="bring-to-school-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up">
          {step === 'done' ? (
            <div className="text-center" data-testid="interest-thank-you">
              <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-[#1F2937] font-['Outfit']">Thank you!</h1>
              <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">
                We've received your interest in bringing CareersPOV to your school.
              </p>
              <Link to="/" className="inline-block mt-6 text-sm text-[#F97D6A] font-medium hover:underline"
                    data-testid="interest-home-link">
                Back to home
              </Link>
            </div>
          ) : step === 'code' ? (
            <form onSubmit={verify} className="space-y-5" data-testid="interest-code-form">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-[#F97D6A]" />
                </div>
                <h1 className="text-xl font-bold text-[#1F2937] font-['Outfit']">Check your email</h1>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">
                  We've sent a 6-digit code to <span className="font-medium text-[#1F2937]">{form.email}</span>.
                  Enter it below to confirm your interest.
                </p>
              </div>
              <div>
                <Label htmlFor="interest-code" className="text-[#1F2937] font-medium">Verification code</Label>
                <Input
                  id="interest-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  placeholder="000000"
                  required
                  className="mt-1.5 rounded-xl h-11 border-gray-200 tracking-[0.4em] text-center text-lg focus:ring-[#F97D6A]/50 focus:border-[#F97D6A]"
                  data-testid="interest-code-input"
                />
              </div>
              <Button type="submit" disabled={code.length !== 6 || busy}
                      className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold"
                      data-testid="interest-verify-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm interest'}
              </Button>
              <button type="button" onClick={resend} disabled={cooldown > 0 || busy}
                      className="w-full text-sm text-[#F97D6A] font-medium hover:underline disabled:text-gray-400 disabled:no-underline"
                      data-testid="interest-resend-btn">
                {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
              </button>
            </form>
          ) : (
            <>
              <div className="text-center mb-7">
                <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Bring CareersPOV to My School</h1>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">
                  Tell us where you'd like to see CareersPOV. We'll pass your interest to our schools team —
                  we don't ask for anything about your child.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-5" data-testid="interest-form">
                <div>
                  <Label htmlFor="interest-school" className="text-[#1F2937] font-medium">School name</Label>
                  <Input id="interest-school" value={form.school_name} onChange={set('school_name')} required
                         placeholder="Your child's school"
                         className="mt-1.5 rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A]"
                         data-testid="interest-school-input" />
                </div>
                <div>
                  <Label htmlFor="interest-city" className="text-[#1F2937] font-medium">City</Label>
                  <Input id="interest-city" value={form.city} onChange={set('city')} required
                         placeholder="City or town"
                         className="mt-1.5 rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A]"
                         data-testid="interest-city-input" />
                </div>
                <div>
                  <Label className="text-[#1F2937] font-medium">Country</Label>
                  <div className="mt-1.5">
                    <CountryPicker value={form.country} onChange={(c) => setForm({ ...form, country: c })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="interest-email" className="text-[#1F2937] font-medium">Email address</Label>
                  <Input id="interest-email" type="email" value={form.email} onChange={set('email')} required
                         placeholder="you@example.com"
                         className="mt-1.5 rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A]"
                         data-testid="interest-email-input" />
                </div>

                {/* honeypot — hidden from people, tempting to bots */}
                <input type="text" name="website" value={form.website} onChange={set('website')}
                       tabIndex={-1} autoComplete="off" aria-hidden="true"
                       className="absolute left-[-9999px] top-auto w-px h-px overflow-hidden" />

                <label className="flex items-start gap-2.5 cursor-pointer bg-[#F9FAFB] border border-gray-200 rounded-xl p-3">
                  <Checkbox checked={guardian} onCheckedChange={setGuardian} className="mt-0.5"
                            data-testid="interest-guardian-checkbox" />
                  <span className="text-sm text-[#1F2937] leading-relaxed">
                    I confirm I am a parent or legal guardian.
                  </span>
                </label>

                <Button type="submit" disabled={!ready || busy}
                        className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold"
                        data-testid="interest-submit-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Continue'}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="text-center mt-6">
          <Link to="/login" className="text-sm text-[#9CA3AF] hover:text-[#4B5563] inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
